import { useCallback } from 'react';

import useMonolithApiMutation, { UseMonolithApiMutationOptions } from '@/hooks/api/useMonolithApiMutation';
import { SimpleErrorResponse } from '@/models/Error';
import { HookInfo } from '@/models/HookInfo';
import { TriggerParams } from '@/models/TriggerParams';
import { startBuildPath } from '@/utils/monolithApiRouteService';

export type StartBuildRequest = {
  payload: {
    hookInfo: HookInfo;
    buildParams: TriggerParams;
    triggeredBy: string;
  };
};

export type StartBuildResponse = {
  errorMsg?: string;
  results?: {
    status: 'ok' | 'pending' | 'error';
    buildNumber?: string;
    buildSlug?: string;
    buildUrl?: string;
    message?: string;
    triggeredWorkflow?: string;
    triggeredPipeline?: string;
  }[];
};

export interface StartBuildErrorResponse extends SimpleErrorResponse {
  results?: StartBuildResponse['results'];
}

type StartBuildProps = {
  appSlug: string;
  buildTriggerToken: string;
  path?: string;
};

const useStartBuild = (
  params: StartBuildProps,
  options?: UseMonolithApiMutationOptions<StartBuildResponse, StartBuildErrorResponse, StartBuildRequest>,
) => {
  const { appSlug, buildTriggerToken, path } = params;

  const { mutate, mutateAsync, ...rest } = useMonolithApiMutation<
    StartBuildResponse,
    SimpleErrorResponse,
    StartBuildRequest
  >(path || startBuildPath(appSlug), 'post', options);

  const newMutate = useCallback(
    (variables: { buildParams: TriggerParams; triggeredBy: string }) =>
      mutate({
        payload: {
          hookInfo: {
            type: 'bitrise',
            buildTriggerToken,
          },
          buildParams: variables.buildParams,
          triggeredBy: variables.triggeredBy,
        },
      }),
    [buildTriggerToken, mutate],
  );

  const newMutateAsync = useCallback(
    (variables: { buildParams: TriggerParams; triggeredBy: string }) =>
      mutateAsync({
        payload: {
          hookInfo: {
            type: 'bitrise',
            buildTriggerToken,
          },
          buildParams: variables.buildParams,
          triggeredBy: variables.triggeredBy,
        },
      }),
    [buildTriggerToken, mutateAsync],
  );

  return {
    ...rest,
    mutate: newMutate,
    mutateAsync: newMutateAsync,
  };
};

export default useStartBuild;
